import { useParams } from 'react-router'
import { Button, Card, Icon, Meter, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { TransactionRow } from '@/components/TransactionRow'
import { Skeleton } from '@/components/States'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { formatMoney } from '@/lib/money'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

const DETAILS: Record<string, ReadonlyArray<readonly [string, string]>> = {
  USD: [['Account holder', 'Alex Turner'], ['Routing number', '084106768'], ['Account number', '9600 0012 4568'], ['Type', 'Checking']],
  EUR: [['Account holder', 'Alex Turner'], ['IBAN', 'BE71 0961 2345 6769'], ['BIC', 'TRWIBEB1XXX'], ['Bank', 'Regulated partner']],
  MXN: [['Beneficiario', 'Alex Turner'], ['CLABE', '646 180 1234 5678 90'], ['Banco', 'SPEI partner'], ['Tipo', 'Depósito']],
  USDC: [['Network', 'Base · Solana'], ['Address', '0x7f3a…9f2a'], ['Yield', 'Rebated by the issuer'], ['Convert fee', '0.10%']],
}

const SETTINGS = [
  { icon: 'label', title: 'Nickname', body: 'Shown across the app' },
  { icon: 'star', title: 'Primary account', body: 'Where incoming money lands' },
  { icon: 'savings', title: 'Round-ups to savings', body: 'Round spare change up' },
  { icon: 'notifications', title: 'Alerts for this account', body: 'Money in and large payments' },
]

export function AccountDetailScreen() {
  const { id } = useParams()
  const repository = useRepository()
  const accounts = useQuery(() => repository.listAccounts(), [repository])
  const transactions = useQuery(() => repository.listTransactions({ limit: 6 }), [repository])

  if (accounts.status === 'loading') return <Skeleton height={420} />
  const account = accounts.data?.find((item) => item.id === id)
  if (!account) return <NotFoundScreen />

  const rows = DETAILS[account.currency] ?? []

  return (
    <>
      <PageHeader
        title={account.label}
        subtitle={`${account.rail} · ${account.maskedNumber}`}
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="south_west" to="/receive">
              Receive
            </Button>
            <Button variant="primary" size="md" iconStart="north_east" to="/transfers/send">
              Send
            </Button>
          </>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Stack direction="row" align="center" gap={3}>
                <CurrencyBadge currency={account.currency} size={40} />
                <Text variant="caption" weight="semibold" tone="onDark">
                  {account.currency}
                </Text>
              </Stack>
              {account.primary && <Pill tone="accent">Primary</Pill>}
            </Card.Header>
            <Stack gap={1} className="pb-mt-4">
              <Text as="p" variant="display" weight="bold" tone="onDark" numeric>
                {formatMoney(account.balance)}
              </Text>
              <Text variant="caption" tone="onDarkMuted">
                Available now · nothing is held
              </Text>
            </Stack>
            <Card.Footer>
              <Stack direction="row" gap={2} wrap>
                <Button size="md" iconStart="add" to="/add-money">
                  Add money
                </Button>
                <Button variant="onDark" size="md" iconStart="currency_exchange" to="/convert">
                  Convert
                </Button>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Card.Header>
              <Text variant="caption" weight="semibold">
                Your details
              </Text>
              <Button variant="ghost" size="sm" iconStart="content_copy">
                Copy
              </Button>
            </Card.Header>
            <Stack gap={0} className="pb-mt-2">
              {rows.map(([label, value]) => (
                <div key={label} className={checkout.line}>
                  <Text variant="caption" tone="secondary">
                    {label}
                  </Text>
                  <Text variant="caption" weight="semibold" numeric>
                    {value}
                  </Text>
                </div>
              ))}
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Card.Header>
              <Text variant="caption" weight="semibold">
                Recent on this account
              </Text>
              <Button variant="ghost" size="sm" iconEnd="arrow_forward" to="/transactions">
                See all
              </Button>
            </Card.Header>
            <div className="pb-mt-3">
              {transactions.data?.map((transaction) => (
                <TransactionRow key={transaction.id} transaction={transaction} />
              ))}
            </div>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Settings
            </Text>
            <div className={`${checkout.reasons} pb-mt-2`}>
              {SETTINGS.map((row) => (
                <button
                  key={row.title}
                  type="button"
                  className={checkout.reason}
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  <Icon name={row.icon} size={20} />
                  <Stack grow gap={1}>
                    <Text variant="caption" weight="semibold">
                      {row.title}
                    </Text>
                    <Text variant="eyebrow" tone="secondary">
                      {row.body}
                    </Text>
                  </Stack>
                  <Icon name="chevron_right" size={18} />
                </button>
              ))}
            </div>
            <Card.Footer>
              <Stack gap={2}>
                <Meter value={0.24} height={6} tone="ink" label="Monthly limit used" />
                <Text variant="eyebrow" tone="secondary">
                  $24,000 of your $100,000 monthly limit used
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
